import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/client';
import MarkdownText from '../components/MarkdownText';
import StudentMessengerCell from '../components/StudentMessengerCell';
import StudentTelemostCell from '../components/StudentTelemostCell';
import StudentCollabCell from '../components/StudentCollabCell';
import StudentAliasCell from '../components/StudentAliasCell';
import SendLessonLinkButton from '../components/SendLessonLinkButton';
import SendSiteNoticeButton from '../components/SendSiteNoticeButton';
import StudentMessageThread from '../components/StudentMessageThread';
import LessonPrepTaskPicker from '../components/LessonPrepTaskPicker';
import ReadOnlyPythonCode from '../components/ReadOnlyPythonCode';
import Modal from '../components/Modal';
import AssignCheckboxList from '../components/AssignCheckboxList';
import TrainerConfigForm, { isTrainerConfigReady, useTrainerKindMeta } from '../components/TrainerConfigForm';
import { taskNavRef } from '../utils/taskNav';
import { agendaItemLabel, taskListLabel } from '../utils/taskListLabel';
import { officialSolutionVariants } from '../utils/baseProgRole';
import LessonJournal from '../components/LessonJournal';
import LessonQuickItemsPanel from '../components/LessonQuickItemsPanel';
import StudentTopicProgressPanel from '../components/StudentTopicProgressPanel';
import { useStudentTabIdentity } from '../hooks/useTabIdentity';
import { addTaskToInbox, fetchInboxByTask, markInboxItemDone } from '../api/taskInbox';
import { studentLabel } from '../utils/studentLabel';

const WORK_TABS = new Set(['student', 'prep', 'journal', 'topics']);
const VIEWING_HEARTBEAT_MS = 3000;

function workTabFromSearch(searchParams) {
  const raw = searchParams.get('tab');
  return WORK_TABS.has(raw) ? raw : 'student';
}

function getCsrfCookie() {
  if (!document.cookie) return '';
  const match = document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith('csrftoken='));
  return match ? decodeURIComponent(match.slice('csrftoken='.length)) : '';
}

/** DELETE viewing, переживает закрытие вкладки (keepalive). */
function clearViewingKeepalive(studentId) {
  const csrf = getCsrfCookie();
  fetch(`/api/teacher/students/${studentId}/viewing/`, {
    method: 'DELETE',
    credentials: 'same-origin',
    keepalive: true,
    headers: {
      ...(csrf ? { 'X-CSRFToken': csrf } : {}),
    },
  }).catch(() => {});
}

const SELF_REPORT_LABELS = {
  EASY: '++',
  OK_NO_PEEK: '+',
  OK_WITH_PEEK: '+-',
  OK_WITH_QUESTION: '+?',
  FAILED: '-',
  NO_IDEAS: '--',
  NOT_TRIED: '!',
};

/** Как problem=1 в журнале активности: −, −−, +? */
const PROBLEM_REPORT_STATUSES = new Set(['FAILED', 'NO_IDEAS', 'OK_WITH_QUESTION']);

const VERDICT_LABELS = {
  ACCEPTED: 'Принять',
  REVIEW_IN_LESSON: 'Посмотрим на занятии',
  REDO: 'Переделать',
};

const DEBOUNCE_MS = 600;

/** Группы для <optgroup> в модалках «Выдать тест» / «Выдать тренажёр». */
function catalogOptgroupLabel(item) {
  if (item.group === 'ege') {
    const key = item.exam_key || '';
    return key ? `ЕГЭ-${String(key).replace('-', '–')}` : 'ЕГЭ';
  }
  if (item.group === 'regex') return 'Регулярки';
  if (item.group === 'excel') return 'Excel';
  return 'Основы';
}

function groupCatalogForSelect(items) {
  const order = [];
  const map = new Map();
  for (const item of items || []) {
    const label = catalogOptgroupLabel(item);
    if (!map.has(label)) {
      map.set(label, []);
      order.push(label);
    }
    map.get(label).push(item);
  }
  return order.map((label) => ({ label, items: map.get(label) }));
}

/** Короткая дата для подписи в окне выдачи (ru-RU). */
function formatCompletionDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/**
 * Подпись «сдан / прочитан» + дата (+ счётчик, если > 1).
 * @param {'сдан'|'прочитан'} verb
 * @param {{ last_finished_at?: string, last_read_at?: string, count?: number }|null} row
 */
function completionMeta(verb, row) {
  if (!row) return '';
  const iso = row.last_finished_at || row.last_read_at;
  const date = formatCompletionDate(iso);
  if (!date) return '';
  const count = Number(row.count) || 0;
  if (count > 1) return `${verb} ${date} · ${count}`;
  return `${verb} ${date}`;
}

function joinMetaParts(...parts) {
  return parts.filter(Boolean).join(' · ');
}

/** DRF next URL → путь относительно /api (как в fetchTaskListAllPages). */
function drfNextToApiRelativePath(nextUrl) {
  try {
    const u = new URL(nextUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const p = u.pathname || '';
    const marker = '/api';
    const idx = p.indexOf(marker);
    const rest = idx >= 0 ? p.slice(idx + marker.length) : p;
    return (rest.startsWith('/') ? rest : `/${rest}`) + (u.search || '');
  } catch {
    return null;
  }
}

async function fetchKonspektyPages(params) {
  const qs = new URLSearchParams(params);
  let path = `/materials/konspekty/?${qs}`;
  const out = [];
  let seriesTags = [];
  let first = true;
  for (;;) {
    const res = await api.get(path);
    const body = res.data;
    if (first) {
      seriesTags = Array.isArray(body?.series_tags) ? body.series_tags : [];
      first = false;
    }
    if (Array.isArray(body)) {
      return { items: body, seriesTags };
    }
    out.push(...(Array.isArray(body?.results) ? body.results : []));
    if (!body?.next) break;
    const nextPath = drfNextToApiRelativePath(body.next);
    if (!nextPath) break;
    path = nextPath;
  }
  return { items: out, seriesTags };
}

/**
 * Страница работы преподавателя с работами ученика.
 * При необходимости можно перевести на тот же блоковый layout (WorkPageBlocksLayout),
 * что и ученическая страница, с дополнительными блоками управления списками и вердиктами.
 */
function fmtListDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function AgendaOrderControls({ index, total, disabled, onMove }) {
  return (
    <span className="agenda-item-order">
      <button
        type="button"
        className="btn btn-outline btn-xs btn-icon-only"
        disabled={disabled || index <= 0}
        onClick={() => onMove('up')}
        title="Выше"
        aria-label="Выше"
      >
        <i className="fa-solid fa-chevron-up ui-icon" aria-hidden />
      </button>
      <button
        type="button"
        className="btn btn-outline btn-xs btn-icon-only"
        disabled={disabled || index >= total - 1}
        onClick={() => onMove('down')}
        title="Ниже"
        aria-label="Ниже"
      >
        <i className="fa-solid fa-chevron-down ui-icon" aria-hidden />
      </button>
    </span>
  );
}

function CollapsibleLeftSection({ sectionId, title, open, onToggle, children }) {
  return (
    <section className={`teacher-work-collapsible ${open ? 'is-open' : 'is-collapsed'}`}>
      <button
        type="button"
        className="teacher-work-collapsible-head"
        aria-expanded={open}
        onClick={() => onToggle(sectionId)}
      >
        <span className="teacher-work-collapsible-chevron" aria-hidden>{open ? '▼' : '▶'}</span>
        <span className="teacher-work-collapsible-title">{title}</span>
      </button>
      {open ? <div className="teacher-work-collapsible-body">{children}</div> : null}
    </section>
  );
}

function mergeOverviewKeepingAgenda(prev, next, keepAgenda) {
  if (!keepAgenda || !prev) return next;
  const prevSession = prev.active_lesson_session;
  const nextSession = next?.active_lesson_session;
  if (!prevSession || !nextSession || prevSession.id !== nextSession.id) return next;
  return {
    ...next,
    active_lesson_session: {
      ...nextSession,
      lesson_agenda: prevSession.lesson_agenda,
      homework_draft: prevSession.homework_draft,
    },
  };
}

export default function TeacherStudentWorkPage() {
  const { studentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listsScope, setListsScope] = useState('mine'); // 'all' | 'mine' | 'other' | 'self'
  const [listsStatusFilter, setListsStatusFilter] = useState('all'); // 'all' | 'in_progress'
  const [leftSectionsOpen, setLeftSectionsOpen] = useState({
    lists: true,
    tasks: true,
    recent: true,
  });
  const [rightSectionsOpen, setRightSectionsOpen] = useState({
    lesson: true,
    draft: true,
    leftover: true,
  });
  const [selectedListId, setSelectedListId] = useState(null);
  const [selectedTaskSlug, setSelectedTaskSlug] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [inboxItemId, setInboxItemId] = useState(null);
  const [inboxBusy, setInboxBusy] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [selectedAttemptId, setSelectedAttemptId] = useState(null);
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentSaved, setCommentSaved] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [officialBusy, setOfficialBusy] = useState(false);
  const [officialMsg, setOfficialMsg] = useState('');
  const [officialError, setOfficialError] = useState('');
  const [etalonVariantIdx, setEtalonVariantIdx] = useState(0);
  const debounceRef = useRef(null);
  const [agendaSaving, setAgendaSaving] = useState(false);
  const [workTab, setWorkTab] = useState(() => workTabFromSearch(searchParams)); // 'student' | 'prep' | 'journal' | 'topics'
  const pendingWorkTabScrollRef = useRef(null);

  const switchWorkTab = (tab) => {
    if (!WORK_TABS.has(tab) || tab === workTab) return;
    pendingWorkTabScrollRef.current = window.scrollY;
    setWorkTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'student') next.delete('tab');
      else next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    const fromUrl = workTabFromSearch(searchParams);
    setWorkTab((prev) => (fromUrl !== prev ? fromUrl : prev));
  }, [searchParams]);

  useLayoutEffect(() => {
    const y = pendingWorkTabScrollRef.current;
    if (y == null) return;
    pendingWorkTabScrollRef.current = null;
    window.scrollTo(0, y);
  }, [workTab]);

  const [topicPlan, setTopicPlan] = useState([]);
  const [topicPlanLoading, setTopicPlanLoading] = useState(false);
  const [topicPlanOpen, setTopicPlanOpen] = useState(false);
  const [listSaving, setListSaving] = useState(false);
  const [listAddOpen, setListAddOpen] = useState(false);
  const [listAddSearch, setListAddSearch] = useState('');
  const [listAddResults, setListAddResults] = useState([]);
  const [listAddSearching, setListAddSearching] = useState(false);
  const viewingIntervalRef = useRef(null);
  const overviewIntervalRef = useRef(null);
  const overviewSeqRef = useRef(0);
  const agendaEpochRef = useRef(0);
  const agendaBusyRef = useRef(0);
  const selectedListIdRef = useRef(null);
  const listsScopeRef = useRef('mine');
  const lessonNotesDebounceRef = useRef(null);
  const [lessonNotesDraft, setLessonNotesDraft] = useState('');
  const [lessonNotesSaved, setLessonNotesSaved] = useState(false);
  const [savingLessonNotes, setSavingLessonNotes] = useState(false);
  const [endingLesson, setEndingLesson] = useState(false);
  const [unlinkingStudent, setUnlinkingStudent] = useState(false);
  const [similarTasks, setSimilarTasks] = useState([]);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueSessionId, setIssueSessionId] = useState(null);
  const [issueTitle, setIssueTitle] = useState('');
  const [issueDeadline, setIssueDeadline] = useState('');
  const [issueSaving, setIssueSaving] = useState(false);
  const [issueAndEnd, setIssueAndEnd] = useState(false);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [problemHwMsg, setProblemHwMsg] = useState('');
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [quizOptions, setQuizOptions] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizSelectedIds, setQuizSelectedIds] = useState([]);
  const [quizTitle, setQuizTitle] = useState('');
  const [quizDeadline, setQuizDeadline] = useState('');
  const [quizShuffle, setQuizShuffle] = useState(false);
  const [quizSaving, setQuizSaving] = useState(false);
  const [materialCompletions, setMaterialCompletions] = useState(null);
  const materialCompletionsLoadedForRef = useRef(null);
  const quizSelectGroups = useMemo(() => groupCatalogForSelect(quizOptions), [quizOptions]);
  const quizById = useMemo(() => {
    const map = new Map();
    for (const row of materialCompletions?.quizzes || []) {
      map.set(String(row.quiz_id), row);
    }
    return map;
  }, [materialCompletions]);
  const quizPickerGroups = useMemo(
    () => quizSelectGroups.map((g) => ({
      label: g.label,
      items: g.items.map((q) => {
        const done = completionMeta('сдан', quizById.get(String(q.id)));
        const countMeta = q.task_count != null ? `${q.task_count} вопр.` : '';
        return {
          id: String(q.id),
          title: q.title,
          meta: joinMetaParts(countMeta, done),
        };
      }),
    })),
    [quizSelectGroups, quizById],
  );
  const [trainerDialogOpen, setTrainerDialogOpen] = useState(false);
  const [trainerSaving, setTrainerSaving] = useState(false);
  const [trainerTitle, setTrainerTitle] = useState('');
  const [trainerDeadline, setTrainerDeadline] = useState('');
  const [trainerConfig, setTrainerConfig] = useState({});
  const [trainerSessions, setTrainerSessions] = useState([]);
  const [trainerAssignments, setTrainerAssignments] = useState([]);
  const [assignNotice, setAssignNotice] = useState('');
  const [trainerSelectedIds, setTrainerSelectedIds] = useState([]);
  const [trainerKinds, setTrainerKinds] = useState([]);
  const trainerSelectGroups = useMemo(() => groupCatalogForSelect(trainerKinds), [trainerKinds]);
  const trainerByKind = useMemo(() => {
    const map = new Map();
    for (const row of materialCompletions?.trainers || []) {
      map.set(String(row.kind), row);
    }
    return map;
  }, [materialCompletions]);
  const trainerPickerGroups = useMemo(
    () => trainerSelectGroups.map((g) => ({
      label: g.label,
      items: g.items.map((k) => ({
        id: k.slug,
        title: k.title,
        meta: joinMetaParts(k.blurb || '', completionMeta('сдан', trainerByKind.get(k.slug))),
      })),
    })),
    [trainerSelectGroups, trainerByKind],
  );
  const singleTrainerKind = trainerSelectedIds.length === 1 ? trainerSelectedIds[0] : null;
  const {
    meta: trainerMeta,
    loading: trainerMetaLoading,
    defaultConfig: trainerDefaultConfig,
  } = useTrainerKindMeta(trainerDialogOpen ? singleTrainerKind : null);
  const [readingDialogOpen, setReadingDialogOpen] = useState(false);
  const [readingOptions, setReadingOptions] = useState([]);
  const [readingSeriesTags, setReadingSeriesTags] = useState([]);
  const [readingTag, setReadingTag] = useState('');
  const [readingLoading, setReadingLoading] = useState(false);
  const [readingSelectedIds, setReadingSelectedIds] = useState([]);
  const [readingSaving, setReadingSaving] = useState(false);
  const [homeworkPackage, setHomeworkPackage] = useState([]);
  const [homeworkPackageBusy, setHomeworkPackageBusy] = useState(false);
  const [journalRefreshNonce, setJournalRefreshNonce] = useState(0);

  const activeSession = overview?.active_lesson_session;
  const leftoverSession = overview?.leftover_homework_session;
  const lastLessonSession = overview?.last_lesson_session;
  const notesSession = activeSession || lastLessonSession;
  const lessonAgenda = activeSession?.lesson_agenda || [];
  const homeworkDraft = activeSession?.homework_draft || [];
  const leftoverDraft = leftoverSession?.homework_draft || [];
  const lessonTaskIdsForPrep = useMemo(
    () => (activeSession?.lesson_agenda || []).map((x) => x.task_id),
    [activeSession?.lesson_agenda],
  );
  const homeworkTaskIdsForPrep = useMemo(
    () => (activeSession?.homework_draft || []).map((x) => x.task_id),
    [activeSession?.homework_draft],
  );

  const loadOverview = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!studentId) return;
    const seq = ++overviewSeqRef.current;
    const agendaEpochAtStart = agendaEpochRef.current;
    const agendaBusyAtStart = agendaBusyRef.current > 0;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const res = await api.get(`/teacher/students/${studentId}/overview/`);
      if (seq !== overviewSeqRef.current) return;
      const keepAgenda = (
        agendaEpochRef.current !== agendaEpochAtStart
        || agendaBusyAtStart
        || agendaBusyRef.current > 0
      );
      setOverview((prev) => mergeOverviewKeepingAgenda(prev, res.data, keepAgenda));
      const mine = Array.isArray(res.data?.task_lists) ? res.data.task_lists : [];
      const other = Array.isArray(res.data?.other_teacher_task_lists) ? res.data.other_teacher_task_lists : [];
      const self = Array.isArray(res.data?.student_self_task_lists) ? res.data.student_self_task_lists : [];
      const allIds = new Set([...mine, ...other, ...self].map((l) => l.id));
      const mergedSorted = [...mine, ...other, ...self].sort((a, b) => {
        const ta = a.issued_at ? new Date(a.issued_at).getTime() : 0;
        const tb = b.issued_at ? new Date(b.issued_at).getTime() : 0;
        return tb - ta;
      });
      const scope = listsScopeRef.current;
      const pool = scope === 'all' ? mergedSorted : scope === 'mine' ? mine : scope === 'other' ? other : self;
      const cur = selectedListIdRef.current;
      if (cur != null && allIds.has(cur)) {
        /* сохраняем выбор списка */
      } else if (pool.length > 0) {
        setSelectedListId(pool[0].id);
      } else {
        setSelectedListId(null);
      }
    } catch (e) {
      if (seq !== overviewSeqRef.current) return;
      if (!silent) {
        const msg = e.response?.data?.detail || e.response?.data?.message || 'Не удалось загрузить данные ученика.';
        setError(typeof msg === 'string' ? msg : 'Не удалось загрузить данные ученика.');
        setOverview(null);
      } else {
        console.error('Teacher overview silent refresh failed', e);
      }
    } finally {
      if (!silent && seq === overviewSeqRef.current) setLoading(false);
    }
  }, [studentId]);

  const loadHomeworkPackage = useCallback(async () => {
    if (!studentId) return;
    try {
      const res = await api.get(`/teacher/students/${studentId}/homework/`);
      setHomeworkPackage(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      setHomeworkPackage([]);
    }
  }, [studentId]);

  const ensureMaterialCompletions = useCallback(async () => {
    if (!studentId) return;
    if (materialCompletionsLoadedForRef.current === studentId) return;
    const forId = studentId;
    materialCompletionsLoadedForRef.current = forId;
    try {
      const res = await api.get(`/teacher/students/${forId}/material-completions/`);
      if (materialCompletionsLoadedForRef.current !== forId) return;
      setMaterialCompletions(res.data || { quizzes: [], trainers: [], konspekts: [] });
    } catch {
      if (materialCompletionsLoadedForRef.current !== forId) return;
      setMaterialCompletions({ quizzes: [], trainers: [], konspekts: [] });
    }
  }, [studentId]);

  useEffect(() => {
    materialCompletionsLoadedForRef.current = null;
    setMaterialCompletions(null);
  }, [studentId]);

  useEffect(() => {
    loadOverview();
    void loadHomeworkPackage();
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;

    const postViewing = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      api.post(`/teacher/students/${studentId}/viewing/`).catch(() => {});
    };

    const clearViewing = () => {
      api.delete(`/teacher/students/${studentId}/viewing/`).catch(() => {});
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (viewingIntervalRef.current) {
          clearInterval(viewingIntervalRef.current);
          viewingIntervalRef.current = null;
        }
        clearViewing();
        return;
      }
      postViewing();
      if (!viewingIntervalRef.current) {
        viewingIntervalRef.current = setInterval(postViewing, VIEWING_HEARTBEAT_MS);
      }
    };

    const onPageHide = () => {
      clearViewingKeepalive(studentId);
    };

    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      postViewing();
      viewingIntervalRef.current = setInterval(postViewing, VIEWING_HEARTBEAT_MS);
    }

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      if (viewingIntervalRef.current) {
        clearInterval(viewingIntervalRef.current);
        viewingIntervalRef.current = null;
      }
      clearViewing();
    };
  }, [studentId]);

  useEffect(() => {
    selectedListIdRef.current = selectedListId;
  }, [selectedListId]);

  useEffect(() => {
    listsScopeRef.current = listsScope;
  }, [listsScope]);

  useEffect(() => {
    const slug = location.state?.selectTaskSlug;
    if (!slug) return;
    setSelectedTaskSlug(slug);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!studentId) return;
    const id = setInterval(() => {
      loadOverview({ silent: true });
    }, 30000);
    overviewIntervalRef.current = id;
    return () => {
      if (id) clearInterval(id);
    };
  }, [studentId, loadOverview]);

  const myLists = useMemo(
    () => (Array.isArray(overview?.task_lists) ? overview.task_lists : []),
    [overview?.task_lists],
  );
  const otherLists = useMemo(
    () => (Array.isArray(overview?.other_teacher_task_lists) ? overview.other_teacher_task_lists : []),
    [overview?.other_teacher_task_lists],
  );
  const selfLists = useMemo(
    () => (Array.isArray(overview?.student_self_task_lists) ? overview.student_self_task_lists : []),
    [overview?.student_self_task_lists],
  );

  const mergedSortedLists = useMemo(() => {
    const merged = [...myLists, ...otherLists, ...selfLists];
    merged.sort((a, b) => {
      const ta = a.issued_at ? new Date(a.issued_at).getTime() : 0;
      const tb = b.issued_at ? new Date(b.issued_at).getTime() : 0;
      return tb - ta;
    });
    return merged;
  }, [myLists, otherLists, selfLists]);

  const rawListsForScope = useMemo(() => {
    if (listsScope === 'all') return mergedSortedLists;
    if (listsScope === 'mine') return myLists;
    if (listsScope === 'other') return otherLists;
    return selfLists;
  }, [listsScope, mergedSortedLists, myLists, otherLists, selfLists]);

  const activeLists = useMemo(() => {
    if (listsStatusFilter === 'in_progress') {
      return rawListsForScope.filter((l) => l.student_status === 'IN_PROGRESS');
    }
    return rawListsForScope;
  }, [rawListsForScope, listsStatusFilter]);

  useEffect(() => {
    if (selectedListId == null) return;
    if (!activeLists.some((l) => l.id === selectedListId)) {
      setSelectedListId(activeLists[0]?.id ?? null);
    }
  }, [activeLists, selectedListId]);

  const selectedList = activeLists.find((l) => l.id === selectedListId)
    || myLists.find((l) => l.id === selectedListId)
    || otherLists.find((l) => l.id === selectedListId)
    || selfLists.find((l) => l.id === selectedListId);
  const tasksInList = selectedList?.items || [];
  const canEditSelectedList = Boolean(
    selectedList && selectedList.teacher_id != null && myLists.some((l) => l.id === selectedList.id),
  );
  const canOpenSelectedList = Boolean(Array.isArray(tasksInList) && tasksInList.length > 0);
  const firstItem = canOpenSelectedList ? tasksInList[0] : null;
  const firstTaskSlug = firstItem?.task ? taskNavRef(firstItem.task) : null;
  const hasActiveLesson = Boolean(activeSession?.id);
  const selectedListWorkUrl = firstTaskSlug && selectedList?.id
    ? (hasActiveLesson
      ? `/sessions/${activeSession.id}/tasks/${firstTaskSlug}?list=${selectedList.id}`
      : `/work/${firstTaskSlug}?list=${selectedList.id}`)
    : null;

  useEffect(() => {
    if (!selectedTaskSlug || !studentId) {
      setTaskDetail(null);
      setAttempts([]);
      setAttemptDetail(null);
      setSelectedAttemptId(null);
      setCommentDraft('');
      setInboxItemId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [taskRes, attemptsRes] = await Promise.all([
          api.get(`/tasks/${selectedTaskSlug}/`),
          api.get(`/teacher/students/${studentId}/attempts/`, { params: { task: selectedTaskSlug } }),
        ]);
        if (cancelled) return;
        setTaskDetail(taskRes.data);
        const list = Array.isArray(attemptsRes.data) ? attemptsRes.data : attemptsRes.data?.results || [];
        setAttempts(list);
        const latest = list[0];
        if (latest) {
          setSelectedAttemptId(latest.id);
          setAttemptDetail(latest);
          setCommentDraft(latest.teacher_comment || '');
        } else {
          setSelectedAttemptId(null);
          setAttemptDetail(null);
          setCommentDraft('');
        }
      } catch {
        if (!cancelled) {
          setTaskDetail(null);
          setAttempts([]);
          setAttemptDetail(null);
          setInboxItemId(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [studentId, selectedTaskSlug]);

  useEffect(() => {
    if (!taskDetail?.id) {
      setInboxItemId(null);
      return undefined;
    }
    let cancelled = false;
    fetchInboxByTask(taskDetail.id)
      .then((data) => {
        if (!cancelled) setInboxItemId(data?.in_inbox ? data.item?.id ?? null : null);
      })
      .catch(() => {
        if (!cancelled) setInboxItemId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [taskDetail?.id]);

  const toggleTaskInbox = useCallback(async () => {
    if (!taskDetail?.id || inboxBusy) return;
    setInboxBusy(true);
    try {
      if (inboxItemId) {
        await markInboxItemDone(inboxItemId);
        setInboxItemId(null);
      } else {
        const item = await addTaskToInbox({
          taskId: taskDetail.id,
          lessonSessionId: activeSession?.id,
          studentId,
        });
        setInboxItemId(item?.id ?? null);
      }
    } catch {
      // оставляем прежнее состояние
    } finally {
      setInboxBusy(false);
    }
  }, [taskDetail?.id, inboxBusy, inboxItemId, activeSession?.id, studentId]);

  useEffect(() => {
    setEtalonVariantIdx(0);
    setOfficialMsg('');
    setOfficialError('');
  }, [taskDetail?.id]);

  const promoteAttemptToOfficial = useCallback(async () => {
    if (officialBusy || !taskDetail || !attemptDetail) return;
    const nextCode = String(attemptDetail.code_snapshot ?? '');
    if (!nextCode.trim()) {
      setOfficialError('Пустой код в эталон не записываем.');
      setTimeout(() => setOfficialError(''), 2000);
      return;
    }
    const hasOfficial = Boolean(String(taskDetail.solution_code ?? '').trim());
    const officialCode = String(taskDetail.solution_code ?? '');
    if (hasOfficial && nextCode === officialCode) return;
    if (hasOfficial) {
      const ok = window.confirm('Заменить эталонное решение кодом этой попытки?');
      if (!ok) return;
    }
    const slug = taskDetail.slug || selectedTaskSlug;
    if (!slug) return;
    setOfficialBusy(true);
    setOfficialError('');
    setOfficialMsg('');
    try {
      const res = await api.patch(`/tasks/editor/${slug}/`, {
        solution_code: nextCode,
      });
      const saved = res.data?.solution_code ?? nextCode;
      setTaskDetail((prev) => (prev ? { ...prev, solution_code: saved } : prev));
      setOfficialMsg(hasOfficial ? 'Эталон заменён' : 'Записано в эталон');
      setTimeout(() => setOfficialMsg(''), 2000);
    } catch (e) {
      const data = e?.response?.data;
      const msg = data?.detail
        || (Array.isArray(data?.non_field_errors) ? data.non_field_errors[0] : null)
        || (typeof data === 'string' ? data : null)
        || 'Не удалось записать эталон.';
      setOfficialError(typeof msg === 'string' ? msg : 'Не удалось записать эталон.');
      setTimeout(() => setOfficialError(''), 3000);
    } finally {
      setOfficialBusy(false);
    }
  }, [officialBusy, taskDetail, attemptDetail, selectedTaskSlug]);

  useEffect(() => {
    if (!selectedTaskSlug) {
      setSimilarTasks([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`/tasks/${selectedTaskSlug}/similar/`)
      .then((res) => {
        if (!cancelled) {
          setSimilarTasks(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) setSimilarTasks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTaskSlug]);

  useEffect(() => {
    if (!attemptDetail) return;
    setCommentDraft(attemptDetail.teacher_comment || '');
  }, [attemptDetail?.id]);

  const saveComment = useCallback(async (text) => {
    if (!studentId || !selectedAttemptId) return;
    setSavingComment(true);
    try {
      await api.patch(`/teacher/students/${studentId}/attempts/${selectedAttemptId}/`, {
        teacher_comment: text,
      });
      setCommentSaved(true);
      setAttemptDetail((prev) => (prev ? { ...prev, teacher_comment: text } : null));
      setTimeout(() => setCommentSaved(false), 2000);
    } catch {
      // keep draft
    } finally {
      setSavingComment(false);
    }
  }, [studentId, selectedAttemptId]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!commentDraft.trim() && !attemptDetail?.teacher_comment) return;
    debounceRef.current = setTimeout(() => {
      saveComment(commentDraft);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [commentDraft, saveComment]);

  const activeLessonSessionId = overview?.active_lesson_session?.id ?? null;
  const notesSessionId = notesSession?.id ?? null;

  useEffect(() => {
    if (!notesSessionId) {
      setLessonNotesDraft('');
      return;
    }
    setLessonNotesDraft(notesSession?.teacher_notes || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только при смене сессии, не затирать черновик при silent refresh
  }, [notesSessionId]);

  useEffect(() => {
    setProblemHwMsg('');
  }, [selectedListId, activeLessonSessionId]);

  const saveLessonNotes = useCallback(async (text) => {
    const sessionId = notesSessionId;
    if (!sessionId) return;
    setSavingLessonNotes(true);
    try {
      await api.patch(`/teacher/lesson-sessions/${sessionId}/`, { teacher_notes: text });
      setLessonNotesSaved(true);
      setOverview((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        if (prev.active_lesson_session?.id === sessionId) {
          next.active_lesson_session = { ...prev.active_lesson_session, teacher_notes: text };
        }
        if (prev.last_lesson_session?.id === sessionId) {
          next.last_lesson_session = { ...prev.last_lesson_session, teacher_notes: text };
        }
        return next;
      });
      setJournalRefreshNonce((n) => n + 1);
      setTimeout(() => setLessonNotesSaved(false), 2000);
    } catch {
      /* оставляем черновик */
    } finally {
      setSavingLessonNotes(false);
    }
  }, [notesSessionId]);

  useEffect(() => {
    if (lessonNotesDebounceRef.current) clearTimeout(lessonNotesDebounceRef.current);
    if (!notesSessionId) return undefined;
    const server = notesSession?.teacher_notes ?? '';
    if (lessonNotesDraft === server) return undefined;
    lessonNotesDebounceRef.current = setTimeout(() => {
      saveLessonNotes(lessonNotesDraft);
    }, DEBOUNCE_MS);
    return () => {
      if (lessonNotesDebounceRef.current) clearTimeout(lessonNotesDebounceRef.current);
    };
  }, [
    lessonNotesDraft,
    notesSessionId,
    notesSession?.teacher_notes,
    saveLessonNotes,
  ]);

  const setVerdict = async (verdict) => {
    if (!studentId || !selectedAttemptId) return;
    try {
      await api.patch(`/teacher/students/${studentId}/attempts/${selectedAttemptId}/`, {
        teacher_verdict: verdict,
      });
      setAttemptDetail((prev) => (prev ? { ...prev, teacher_verdict: verdict } : null));
    } catch (e) {
      console.error(e);
    }
  };

  async function loadTopicPlan() {
    if (!studentId) return;
    setTopicPlanLoading(true);
    try {
      const res = await api.get(`/teacher/students/${studentId}/topic-plan/`);
      setTopicPlan(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTopicPlan([]);
    } finally {
      setTopicPlanLoading(false);
    }
  }

  const applyAgendaSession = (sessionData) => {
    agendaEpochRef.current += 1;
    setOverview((prev) => {
      if (!prev) return prev;
      const session = { ...prev.active_lesson_session, ...sessionData };
      return { ...prev, active_lesson_session: session };
    });
  };

  const addToAgenda = async (type, taskId) => {
    if (!activeSession?.id) return;
    const raw = type === 'lesson' ? (activeSession.lesson_agenda || []) : (activeSession.homework_draft || []);
    if (raw.some((x) => x.task_id === taskId)) return;
    const list = [
      ...raw.map((x, i) => ({ task_id: x.task_id, position: i })),
      { task_id: taskId, position: raw.length },
    ];
    agendaBusyRef.current += 1;
    setAgendaSaving(true);
    try {
      const res = await api.patch(`/teacher/lesson-sessions/${activeSession.id}/`, type === 'lesson' ? { lesson_agenda: list } : { homework_draft: list });
      applyAgendaSession(res.data);
    } finally {
      agendaBusyRef.current -= 1;
      setAgendaSaving(false);
    }
  };

  const moveInAgenda = async (type, index, direction) => {
    if (!activeSession?.id) return;
    const raw = type === 'lesson'
      ? [...(activeSession.lesson_agenda || [])]
      : [...(activeSession.homework_draft || [])];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= raw.length) return;
    const next = [...raw];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    const list = next.map((x, i) => ({ task_id: x.task_id, position: i }));
    agendaBusyRef.current += 1;
    setAgendaSaving(true);
    try {
      const res = await api.patch(
        `/teacher/lesson-sessions/${activeSession.id}/`,
        type === 'lesson' ? { lesson_agenda: list } : { homework_draft: list },
      );
      applyAgendaSession(res.data);
    } finally {
      agendaBusyRef.current -= 1;
      setAgendaSaving(false);
    }
  };

  const removeFromAgenda = async (type, taskId) => {
    if (!activeSession?.id) return;
    const list = (type === 'lesson' ? [...(activeSession.lesson_agenda || [])] : [...(activeSession.homework_draft || [])])
      .filter((x) => x.task_id !== taskId)
      .map((x, i) => ({ task_id: x.task_id, position: i }));
    agendaBusyRef.current += 1;
    setAgendaSaving(true);
    try {
      const res = await api.patch(`/teacher/lesson-sessions/${activeSession.id}/`, type === 'lesson' ? { lesson_agenda: list } : { homework_draft: list });
      applyAgendaSession(res.data);
    } finally {
      agendaBusyRef.current -= 1;
      setAgendaSaving(false);
    }
  };

  const addProblemHwToLesson = async () => {
    if (!activeSession?.id) {
      setProblemHwMsg('Нет активного урока');
      return;
    }
    if (!selectedList?.items?.length) {
      setProblemHwMsg('Нечего добавлять');
      return;
    }
    const existing = new Set((activeSession.lesson_agenda || []).map((x) => x.task_id));
    const toAdd = [];
    for (const item of selectedList.items) {
      const taskId = item.task?.id ?? item.task_id;
      if (taskId == null || existing.has(taskId)) continue;
      const report = item.latest_attempt?.self_report_status ?? item.latest_self_report?.self_report_status;
      if (!PROBLEM_REPORT_STATUSES.has(report)) continue;
      toAdd.push(taskId);
      existing.add(taskId);
    }
    if (toAdd.length === 0) {
      setProblemHwMsg('Нечего добавлять');
      return;
    }
    const raw = activeSession.lesson_agenda || [];
    const list = [
      ...raw.map((x, i) => ({ task_id: x.task_id, position: i })),
      ...toAdd.map((taskId, i) => ({ task_id: taskId, position: raw.length + i })),
    ];
    agendaBusyRef.current += 1;
    setAgendaSaving(true);
    setProblemHwMsg('');
    try {
      const res = await api.patch(`/teacher/lesson-sessions/${activeSession.id}/`, { lesson_agenda: list });
      applyAgendaSession(res.data);
      setProblemHwMsg(`Добавлено ${toAdd.length}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось добавить задачи.';
      setProblemHwMsg(typeof msg === 'string' ? msg : 'Не удалось добавить задачи.');
    } finally {
      agendaBusyRef.current -= 1;
      setAgendaSaving(false);
    }
  };

  function bumpJournalRefresh() {
    setJournalRefreshNonce((n) => n + 1);
  }

  function defaultHomeworkTitle() {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    return `ДЗ ${dd}.${mm}`;
  }

  function openIssueDialog(sessionId, { andEnd = false } = {}) {
    setIssueSessionId(sessionId);
    setIssueTitle(defaultHomeworkTitle());
    setIssueDeadline('');
    setIssueAndEnd(andEnd);
    setIssueDialogOpen(true);
  }

  async function submitIssueHomework() {
    if (!issueSessionId) return;
    setIssueSaving(true);
    try {
      const payload = { title: issueTitle.trim() || defaultHomeworkTitle() };
      if (issueDeadline) {
        payload.deadline_at = `${issueDeadline}T23:59:59`;
      }
      await api.post(`/teacher/lesson-sessions/${issueSessionId}/issue-homework/`, payload);
      if (issueAndEnd) {
        await api.post(`/tasks/lesson-sessions/${issueSessionId}/end/`);
      }
      setIssueDialogOpen(false);
      setEndDialogOpen(false);
      setIssueSessionId(null);
      setIssueAndEnd(false);
      await loadOverview({ silent: true });
      await loadHomeworkPackage();
      bumpJournalRefresh();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось выдать задачи.';
      alert(typeof msg === 'string' ? msg : 'Не удалось выдать задачи.');
    } finally {
      setIssueSaving(false);
    }
  }

  async function endLessonWithoutIssue() {
    if (!activeSession?.id) return;
    setEndingLesson(true);
    try {
      await api.post(`/tasks/lesson-sessions/${activeSession.id}/end/`);
      setEndDialogOpen(false);
      await loadOverview({ silent: true });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось завершить урок.';
      alert(typeof msg === 'string' ? msg : 'Не удалось завершить урок.');
    } finally {
      setEndingLesson(false);
    }
  }

  async function clearLeftoverDraft() {
    if (!leftoverSession?.id) return;
    if (!window.confirm('Очистить невыданный черновик ДЗ?')) return;
    setAgendaSaving(true);
    try {
      await api.patch(`/teacher/lesson-sessions/${leftoverSession.id}/`, { homework_draft: [] });
      await loadOverview({ silent: true });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось очистить черновик.';
      alert(typeof msg === 'string' ? msg : 'Не удалось очистить черновик.');
    } finally {
      setAgendaSaving(false);
    }
  }

  async function openQuizDialog() {
    setQuizDialogOpen(true);
    setQuizSelectedIds([]);
    setQuizTitle('');
    setQuizDeadline('');
    setQuizShuffle(false);
    setQuizLoading(true);
    void ensureMaterialCompletions();
    try {
      const res = await api.get('/tasks/practice-quizzes/');
      const list = Array.isArray(res.data) ? res.data : [];
      setQuizOptions(list);
    } catch {
      setQuizOptions([]);
      alert('Не удалось загрузить список тестов.');
    } finally {
      setQuizLoading(false);
    }
  }

  function handleQuizSelection(ids) {
    setQuizSelectedIds(ids);
    if (ids.length === 1) {
      const found = quizOptions.find((q) => String(q.id) === String(ids[0]));
      setQuizTitle(found?.title || '');
    } else {
      setQuizTitle('');
    }
  }

  async function submitIssueQuiz() {
    if (!studentId || quizSelectedIds.length === 0) return;
    setQuizSaving(true);
    let lastNewId = null;
    const issuedTitles = [];
    try {
      for (const id of quizSelectedIds) {
        const quiz = quizOptions.find((q) => String(q.id) === String(id));
        const payload = {
          quiz_id: Number(id),
          shuffle: quizShuffle,
        };
        if (quizSelectedIds.length === 1 && quizTitle.trim()) {
          payload.title = quizTitle.trim();
        }
        if (quizDeadline) payload.deadline_at = `${quizDeadline}T23:59:59`;
        const res = await api.post(`/teacher/students/${studentId}/issue-quiz/`, payload);
        const newList = res.data?.task_list;
        lastNewId = newList?.id ?? lastNewId;
        issuedTitles.push(newList?.title || quiz?.title || 'Тест');
      }
      setQuizDialogOpen(false);
      setListsScope('mine');
      setListsStatusFilter('all');
      listsScopeRef.current = 'mine';
      await loadOverview({ silent: true });
      if (lastNewId != null) setSelectedListId(lastNewId);
      await loadHomeworkPackage();
      bumpJournalRefresh();
      if (issuedTitles.length === 1) {
        setAssignNotice(`Тест «${issuedTitles[0]}» выдан — он в журнале и в списках задач.`);
      } else {
        setAssignNotice(`Выдано тестов: ${issuedTitles.length}. Они в журнале и в списках задач.`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось выдать тест.';
      alert(typeof msg === 'string' ? msg : 'Не удалось выдать тест.');
      if (issuedTitles.length > 0) {
        await loadOverview({ silent: true });
        if (lastNewId != null) setSelectedListId(lastNewId);
      }
    } finally {
      setQuizSaving(false);
    }
  }

  useEffect(() => {
    if (trainerDialogOpen && singleTrainerKind && trainerDefaultConfig && Object.keys(trainerDefaultConfig).length) {
      setTrainerConfig({ ...trainerDefaultConfig });
    }
  }, [trainerDialogOpen, singleTrainerKind, trainerDefaultConfig]);

  useEffect(() => {
    if (!trainerDialogOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/trainers/catalog/');
        if (!cancelled) {
          const implemented = (Array.isArray(res.data) ? res.data : []).filter((x) => x.implemented);
          setTrainerKinds(implemented);
        }
      } catch {
        if (!cancelled) setTrainerKinds([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trainerDialogOpen]);

  const loadTrainerOverview = useCallback(async () => {
    if (!studentId) return;
    try {
      const [asgRes, sessRes] = await Promise.all([
        api.get(`/trainers/students/${studentId}/assignments/`),
        api.get(`/trainers/students/${studentId}/sessions/`),
      ]);
      setTrainerAssignments(Array.isArray(asgRes.data) ? asgRes.data : []);
      setTrainerSessions(Array.isArray(sessRes.data) ? sessRes.data : []);
    } catch {
      setTrainerAssignments([]);
      setTrainerSessions([]);
    }
  }, [studentId]);

  useEffect(() => {
    void loadTrainerOverview();
  }, [loadTrainerOverview]);

  function openTrainerDialog() {
    setTrainerDialogOpen(true);
    setTrainerSelectedIds([]);
    setTrainerTitle('');
    setTrainerDeadline('');
    setTrainerConfig({});
    void ensureMaterialCompletions();
  }

  function handleTrainerSelection(ids) {
    setTrainerSelectedIds(ids);
    if (ids.length === 1) {
      const found = trainerKinds.find((k) => k.slug === ids[0]);
      setTrainerTitle(found?.title || '');
    } else {
      setTrainerTitle('');
      setTrainerConfig({});
    }
  }

  async function submitIssueTrainer() {
    if (!studentId || trainerSelectedIds.length === 0) return;
    if (trainerSelectedIds.length === 1) {
      if (!isTrainerConfigReady(trainerMeta, trainerConfig)) return;
    }
    setTrainerSaving(true);
    const issuedTitles = [];
    try {
      for (const kind of trainerSelectedIds) {
        let config;
        let targetCount;
        if (trainerSelectedIds.length === 1) {
          config = trainerConfig;
          targetCount = trainerConfig.target_count || 10;
        } else {
          const metaRes = await api.get(`/trainers/kinds/${kind}/`);
          config = { ...(metaRes.data?.default_config || {}) };
          targetCount = config.target_count || 10;
        }
        const payload = {
          student_id: Number(studentId),
          kind,
          config,
          target_count: targetCount,
        };
        if (trainerSelectedIds.length === 1 && trainerTitle.trim()) {
          payload.title = trainerTitle.trim();
        }
        if (trainerDeadline) payload.deadline_at = `${trainerDeadline}T23:59:59`;
        const res = await api.post('/trainers/assignments/create/', payload);
        issuedTitles.push(res.data?.display_title || res.data?.title || kind);
      }
      setTrainerDialogOpen(false);
      await loadTrainerOverview();
      if (issuedTitles.length === 1) {
        setAssignNotice(`Тренажёр «${issuedTitles[0]}» выдан — в журнале и в разделе «Тренажёры».`);
      } else {
        setAssignNotice(`Выдано тренажёров: ${issuedTitles.length}. В журнале и в «Тренажёры».`);
      }
      await loadHomeworkPackage();
      bumpJournalRefresh();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось выдать тренажёр.';
      alert(typeof msg === 'string' ? msg : 'Не удалось выдать тренажёр.');
      if (issuedTitles.length > 0) await loadTrainerOverview();
    } finally {
      setTrainerSaving(false);
    }
  }

  async function loadReadingOptions(tag) {
    setReadingLoading(true);
    try {
      const params = { scope: 'all', page_size: '100' };
      if (tag) params.tag = tag;
      const { items, seriesTags } = await fetchKonspektyPages(params);
      setReadingOptions(items);
      if (seriesTags.length) setReadingSeriesTags(seriesTags);
    } catch {
      setReadingOptions([]);
      alert('Не удалось загрузить конспекты.');
    } finally {
      setReadingLoading(false);
    }
  }

  async function openReadingDialog() {
    setReadingDialogOpen(true);
    setReadingSelectedIds([]);
    setReadingTag('');
    void ensureMaterialCompletions();
    await loadReadingOptions('');
  }

  async function selectReadingTag(slug) {
    const next = readingTag === slug ? '' : slug;
    setReadingTag(next);
    setReadingSelectedIds([]);
    await loadReadingOptions(next);
  }

  const readingPickerGroups = useMemo(() => {
    const alreadyIds = new Set(
      (homeworkPackage || [])
        .filter((h) => h.kind === 'konspekt')
        .map((h) => String(h.block_id || ''))
        .filter(Boolean),
    );
    const alreadySlugs = new Set(
      (homeworkPackage || [])
        .filter((h) => h.kind === 'konspekt')
        .map((h) => h.block_slug)
        .filter(Boolean),
    );
    const readByBlock = new Map();
    for (const row of materialCompletions?.konspekts || []) {
      readByBlock.set(String(row.block_id), row);
    }
    const items = (readingOptions || []).map((k) => {
      const already = alreadyIds.has(String(k.id)) || alreadySlugs.has(k.slug);
      return {
        id: String(k.id),
        title: k.title || k.slug,
        meta: completionMeta('прочитан', readByBlock.get(String(k.id))),
        disabled: already,
        disabledLabel: already ? 'В ДЗ' : '',
      };
    });
    return [{ label: '', items }];
  }, [readingOptions, homeworkPackage, materialCompletions]);

  async function submitAddReadings() {
    if (!studentId || readingSelectedIds.length === 0) return;
    setReadingSaving(true);
    let added = 0;
    try {
      for (const blockId of readingSelectedIds) {
        await api.post(`/teacher/students/${studentId}/homework/`, {
          block_id: Number(blockId),
        });
        added += 1;
      }
      setReadingSelectedIds([]);
      await loadHomeworkPackage();
      bumpJournalRefresh();
      setAssignNotice(
        added === 1
          ? 'Конспект выдан — он в журнале.'
          : `Выдано конспектов: ${added}.`,
      );
      setReadingDialogOpen(false);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось выдать конспект.';
      alert(typeof msg === 'string' ? msg : 'Не удалось выдать конспект.');
      if (added > 0) {
        await loadHomeworkPackage();
        bumpJournalRefresh();
      }
    } finally {
      setReadingSaving(false);
    }
  }

  async function removeHomeworkItem(item) {
    const itemId = item?.id;
    if (!itemId) return;
    const title = item.title || 'этот пункт';
    const msg = item.kind === 'quiz'
      ? `Убрать тест «${title}»? Он исчезнет из журнала и из списков ученика.`
      : `Убрать «${title}» из домашнего задания?`;
    if (!window.confirm(msg)) return;
    setHomeworkPackageBusy(true);
    try {
      await api.delete(`/teacher/homework-items/${itemId}/`);
      await loadHomeworkPackage();
      await loadOverview({ silent: true });
      bumpJournalRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Не удалось убрать пункт ДЗ.';
      alert(typeof detail === 'string' ? detail : 'Не удалось убрать пункт ДЗ.');
    } finally {
      setHomeworkPackageBusy(false);
    }
  }

  async function moveHomeworkItem(itemId, direction) {
    if (!itemId) return;
    setHomeworkPackageBusy(true);
    try {
      await api.patch(`/teacher/homework-items/${itemId}/`, { direction });
      await loadHomeworkPackage();
      bumpJournalRefresh();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось изменить порядок.';
      alert(typeof msg === 'string' ? msg : 'Не удалось изменить порядок.');
    } finally {
      setHomeworkPackageBusy(false);
    }
  }

  function requestEndLesson() {
    if (!activeSession?.id) return;
    if ((activeSession.homework_draft || []).length > 0) {
      setEndDialogOpen(true);
      return;
    }
    if (!window.confirm('Завершить урок? После этого можно начать новый.')) return;
    void endLessonWithoutIssue();
  }

  async function handleUnlinkStudent() {
    if (!overview?.student) return;
    const label = studentLabel(overview.student) || overview.student.username || 'ученика';
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Убрать ${label} из списка учеников?\n\nЖурналы занятий и оплаты сохранятся.`,
    )) return;
    setUnlinkingStudent(true);
    try {
      await api.post(`/teacher/students/${studentId}/unlink/`);
      navigate('/teacher');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось убрать ученика.';
      // eslint-disable-next-line no-alert
      alert(typeof msg === 'string' ? msg : 'Не удалось убрать ученика.');
    } finally {
      setUnlinkingStudent(false);
    }
  }

  function buildTaskListItemsPayload(items) {
    return (items || []).map((it, i) => ({
      task_id: it.task?.id ?? it.task_id,
      position: i,
      teacher_note: it.teacher_note ?? '',
    }));
  }

  const removeFromTaskList = async (taskId) => {
    if (!selectedListId || !selectedList?.items) return;
    const newItems = selectedList.items
      .filter((it) => (it.task?.id ?? it.task_id) !== taskId)
      .map((it, i) => ({ ...it, position: i }));
    const payload = { items: buildTaskListItemsPayload(newItems) };
    setListSaving(true);
    try {
      await api.patch(`/teacher/task-lists/${selectedListId}/`, payload);
      // Без полного loading: иначе размонтируется «Подготовка» и слетает поиск/теги.
      setOverview((prev) => {
        if (!prev) return prev;
        const patchList = (lists) =>
          (Array.isArray(lists) ? lists : []).map((lst) =>
            lst.id === selectedListId ? { ...lst, items: newItems } : lst,
          );
        return {
          ...prev,
          task_lists: patchList(prev.task_lists),
          other_teacher_task_lists: patchList(prev.other_teacher_task_lists),
          student_self_task_lists: patchList(prev.student_self_task_lists),
        };
      });
    } catch (e) {
      console.error('Remove from task list failed', e);
    } finally {
      setListSaving(false);
    }
  };

  const addToTaskList = async (taskId) => {
    if (!selectedListId || !selectedList?.items) return;
    const existingIds = selectedList.items.map((it) => it.task?.id ?? it.task_id);
    if (existingIds.includes(taskId)) return;
    const newItems = [
      ...selectedList.items,
      { task_id: taskId, position: selectedList.items.length, teacher_note: '' },
    ];
    const payload = { items: buildTaskListItemsPayload(newItems) };
    setListSaving(true);
    try {
      await api.patch(`/teacher/task-lists/${selectedListId}/`, payload);
      await loadOverview({ silent: true });
      setListAddResults((prev) => prev.filter((t) => t.id !== taskId));
    } catch (e) {
      console.error('Add to task list failed', e);
    } finally {
      setListSaving(false);
    }
  };

  async function searchListAddTasks() {
    if (!listAddSearch.trim()) {
      setListAddResults([]);
      return;
    }
    setListAddSearching(true);
    try {
      const res = await api.get('/teacher/tasks-for-list/', { params: { search: listAddSearch.trim() } });
      setListAddResults(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch {
      setListAddResults([]);
    } finally {
      setListAddSearching(false);
    }
  }

  const currentTaskId = taskDetail?.id;
  const etalonVariants = officialSolutionVariants(taskDetail);
  const activeEtalon = etalonVariants[etalonVariantIdx] || etalonVariants[0];
  const attemptCodeStr = String(attemptDetail?.code_snapshot ?? '');
  const officialCodeStr = String(taskDetail?.solution_code ?? '');
  const hasOfficial = Boolean(officialCodeStr.trim());
  const officialSynced = hasOfficial && attemptCodeStr === officialCodeStr;
  const officialBtnTitle = officialBusy
    ? 'Сохранение…'
    : officialSynced
      ? 'Этот код уже в эталоне'
      : hasOfficial
        ? 'Заменить эталон кодом этой попытки'
        : 'В эталон (если его ещё нет)';
  const officialOverlayMsg = officialError || officialMsg;

  useStudentTabIdentity(overview?.student ?? null, 'Урок');

  if (loading && !overview) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Загрузка...</p>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="teacher-student-work-page error-state">
        <p>{error || 'Ученик не найден.'}</p>
        <Link to="/teacher" className="btn btn-outline">В кабинет преподавателя</Link>
      </div>
    );
  }

  const relationActive = overview.relation_status === 'active';

  return (
    <div className="teacher-student-work-page teacher-student-work-page--workspace">
      {!relationActive && (
        <div className="alert alert-warning" role="alert">
          Связь не активна ({overview.relation_status || 'ожидает подтверждения'}). Кнопка «Начать урок» и добавление задач в списки занятия/ДЗ доступны только при активной связи.
        </div>
      )}
      <header className="teacher-student-work-header">
        <Link to="/teacher" className="back-link">← Кабинет</Link>
        <StudentAliasCell
          as="h1"
          studentId={studentId}
          student={overview.student}
          onUpdated={(alias) => {
            setOverview((prev) => (prev
              ? {
                  ...prev,
                  alias,
                  student: { ...prev.student, alias },
                }
              : prev));
          }}
        />
        <StudentMessengerCell
          studentId={studentId}
          messenger={overview.messenger}
          onUpdated={(messenger) => {
            setOverview((prev) => (prev ? { ...prev, messenger } : prev));
          }}
        />
        <StudentTelemostCell
          studentId={studentId}
          telemostUrl={overview.telemost_url}
          onUpdated={(telemost_url) => {
            setOverview((prev) => (prev ? { ...prev, telemost_url } : prev));
          }}
        />
        <StudentCollabCell collabSessionId={overview.collab_session_id} />
        <SendLessonLinkButton
          studentId={studentId}
          studentName={overview.student?.alias || overview.student?.first_name || overview.student?.username}
          initialMeetingUrl={overview.telemost_url || ''}
        />
        <SendSiteNoticeButton
          studentId={studentId}
          studentName={overview.student?.alias || overview.student?.first_name || overview.student?.username}
        />
        {overview.is_online != null && (
          <span className={`presence-dot ${overview.is_online ? 'online' : 'offline'}`} title={overview.is_online ? 'Ученик в сети' : 'Не в сети'} />
        )}
        {overview.current_task_slug && (
          <span className="text-secondary current-task-hint">Задача: {overview.current_task_slug}</span>
        )}
        {overview.work_join_url ? (
          <Link to={overview.work_join_url} className="btn btn-sm btn-primary">
            Подключиться к работе
          </Link>
        ) : (
          <span
            className="text-secondary work-join-unavailable"
            style={{ fontSize: '0.9rem', maxWidth: '22rem' }}
            title="Нужна страница работы с параметром list и активная совместная сессия редактора"
          >
            Подключение недоступно: пусть ученик откроет задачу из списка урока (баннер «Урок идёт» или «Мои списки»).
          </span>
        )}
        <Link
          to={`/teacher/students/${studentId}/questionnaire`}
          className="btn btn-sm btn-outline"
        >
          {overview.student.has_questionnaire ? 'Анкета' : 'Анкета (нет)'}
        </Link>
        <Link
          to={`/teacher/students/${studentId}/activity`}
          className="btn btn-sm btn-outline"
        >
          Журнал активности
        </Link>
        <Link
          to={`/teacher/billing/${studentId}`}
          className="btn btn-sm btn-outline"
        >
          Оплата
        </Link>
        {activeSession ? (
          <>
            <span className="badge session-badge">
              Урок #{activeSession.id}
            </span>
            {activeSession.work_list_id ? (
              <Link
                to={
                  activeSession.work_list_first_task_slug
                    ? `/work/${activeSession.work_list_first_task_slug}?list=${activeSession.work_list_id}`
                    : `/tasks`
                }
                className="btn btn-sm btn-outline"
                title={activeSession.work_list_title || 'Список урока'}
              >
                {activeSession.work_list_first_task_slug
                  ? `Список урока: ${activeSession.work_list_title || 'открыть'}`
                  : `${activeSession.work_list_title || 'Список урока'} (пока пуст)`}
              </Link>
            ) : null}
            <button
              type="button"
              className="btn btn-sm btn-outline"
              disabled={endingLesson}
              title="Завершить текущую сессию урока"
              onClick={requestEndLesson}
            >
              {endingLesson ? '…' : 'Завершить урок'}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={!relationActive}
            title={!relationActive ? 'Связь с учеником должна быть активной' : ''}
            onClick={async () => {
              if (!relationActive) return;
              try {
                await api.post('/teacher/lesson-sessions/', { student_id: Number(studentId) });
                await loadOverview({ silent: true });
              } catch (e) {
                const msg = e.response?.data?.detail || 'Не удалось начать урок.';
                alert(typeof msg === 'string' ? msg : 'Не удалось начать урок.');
              }
            }}
          >
            Начать урок
          </button>
        )}
        {overview.relation_status !== 'blocked' && (
          <button
            type="button"
            className="btn btn-sm btn-outline"
            disabled={unlinkingStudent}
            title="Убрать ученика из вашего списка"
            onClick={() => void handleUnlinkStudent()}
          >
            {unlinkingStudent ? '…' : 'Убрать из списка'}
          </button>
        )}
      </header>

      {relationActive && (
        <StudentMessageThread
          mode="teacher"
          studentId={Number(studentId)}
          studentName={overview.student?.alias || overview.student?.first_name || overview.student?.username}
        />
      )}

      <div className="teacher-work-tabs" role="tablist" aria-label="Режим работы">
        <button
          type="button"
          role="tab"
          aria-selected={workTab === 'student'}
          className={`tab-btn ${workTab === 'student' ? 'active' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => switchWorkTab('student')}
        >
          Работа с учеником
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={workTab === 'prep'}
          className={`tab-btn ${workTab === 'prep' ? 'active' : ''}`}
          title="Найдите задачу по тексту или тегу и добавьте в «Задачи для занятия» или «Черновик списка задач» справа. Тесты и конспекты — во вкладке «Журнал». Можно отфильтровать по прогрессу этого ученика."
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => switchWorkTab('prep')}
        >
          Подготовка (занятие / ДЗ)
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={workTab === 'journal'}
          className={`tab-btn ${workTab === 'journal' ? 'active' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => switchWorkTab('journal')}
        >
          Журнал занятий
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={workTab === 'topics'}
          className={`tab-btn ${workTab === 'topics' ? 'active' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => switchWorkTab('topics')}
        >
          Прогресс по темам
        </button>
      </div>

      <div className="teacher-work-tab-panels">
      <div
        className="teacher-journal-tab"
        hidden={workTab !== 'journal'}
        inert={workTab !== 'journal'}
      >
          <p className="text-secondary" style={{ marginTop: 0 }}>
            Выдача ДЗ (тесты, конспекты, тренажёры) и комментарий к текущему уроку —
            здесь же, над карточками. Ниже — как журнал видит ученик.
            Клик по номеру задачи открывает её во вкладке «Работа с учеником».
          </p>

          <div className="teacher-journal-issue-panel">
            {activeSession ? (
              <>
                <div className="agenda-block lesson-notes-block">
                  <h4>Заметки по уроку</h4>
                  <p className="text-secondary" style={{ fontSize: '0.85rem', margin: '0 0 0.35rem' }}>
                    Ученик видит это как комментарий преподавателя на карточке урока. Сохраняется автоматически.
                  </p>
                  <textarea
                    className="form-input"
                    rows={4}
                    value={lessonNotesDraft}
                    onChange={(e) => setLessonNotesDraft(e.target.value)}
                    placeholder="Краткий отчёт или планы — сохраняется автоматически"
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                  <span className="save-indicator">
                    {savingLessonNotes ? 'Сохранение…' : lessonNotesSaved ? '✓ Сохранено' : ''}
                  </span>
                </div>
                <div className="agenda-block">
                  <h4>Выдать ДЗ</h4>
                  {assignNotice ? (
                    <p className="alert alert-success assign-notice" role="status">{assignNotice}</p>
                  ) : (
                    <p className="text-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                      Сразу попадает в карточку урока ниже (блок «Домашнее задание»). Завершать урок не нужно.
                      Порядок — стрелками у пунктов без дедлайна.
                    </p>
                  )}
                  <div className="assign-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      disabled={!relationActive || readingSaving || readingLoading}
                      title={!relationActive ? 'Нужна активная связь' : 'Выдать конспект'}
                      onClick={() => void openReadingDialog()}
                    >
                      Выдать конспект
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      disabled={!relationActive || quizSaving}
                      title={!relationActive ? 'Нужна активная связь' : 'Выдать тест'}
                      onClick={() => void openQuizDialog()}
                    >
                      Выдать тест
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      disabled={!relationActive || trainerSaving}
                      title={!relationActive ? 'Нужна активная связь' : 'Выдать тренажёр'}
                      onClick={() => openTrainerDialog()}
                    >
                      Выдать тренажёр
                    </button>
                  </div>
                  {homeworkPackage.length > 0 && (
                    <ul className="agenda-list agenda-list-editable homework-package-list" style={{ marginTop: '0.75rem' }}>
                      {homeworkPackage.map((item) => {
                        const noDeadline = !item.deadline_at;
                        const orderedSiblings = homeworkPackage.filter((h) => !h.deadline_at);
                        const ordIdx = orderedSiblings.findIndex((h) => h.id === item.id);
                        return (
                          <li key={item.id}>
                            <span className="text-secondary" style={{ fontSize: '0.75rem', marginRight: '0.35rem' }}>
                              {item.kind_label || item.kind}
                            </span>
                            <span>{item.title}</span>
                            {item.deadline_at ? (
                              <span className="text-secondary" style={{ marginLeft: '0.35rem', fontSize: '0.85rem' }}>
                                до {new Date(item.deadline_at).toLocaleDateString('ru-RU')}
                              </span>
                            ) : null}
                            {item.status_label ? (
                              <span className="text-secondary" style={{ marginLeft: '0.35rem', fontSize: '0.85rem' }}>
                                · {item.status_label}
                              </span>
                            ) : null}
                            {noDeadline && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-xs btn-outline"
                                  title="Выше"
                                  disabled={homeworkPackageBusy || ordIdx <= 0}
                                  onClick={() => void moveHomeworkItem(item.id, 'up')}
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-xs btn-outline"
                                  title="Ниже"
                                  disabled={homeworkPackageBusy || ordIdx < 0 || ordIdx >= orderedSiblings.length - 1}
                                  onClick={() => void moveHomeworkItem(item.id, 'down')}
                                >
                                  ↓
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              className="btn btn-xs btn-outline agenda-remove"
                              title={item.kind === 'quiz' ? 'Убрать тест у ученика' : 'Убрать из ДЗ'}
                              disabled={homeworkPackageBusy}
                              onClick={() => void removeHomeworkItem(item)}
                            >
                              ✕
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {trainerSessions.length > 0 && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <h4 style={{ margin: '0 0 0.35rem' }}>Сессии тренажёров</h4>
                      <ul className="agenda-list">
                        {trainerSessions.slice(0, 5).map((s) => (
                          <li key={s.id}>
                            <Link to={`/trainers/sessions/${s.id}`} className="link-like">
                              {s.kind_title || s.kind}
                            </Link>
                            <span className="text-secondary">
                              {' '}
                              · {s.problems_correct}/{s.target_count}
                              {s.status === 'finished' ? '' : ' · идёт'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="agenda-block">
                <p className="text-secondary" style={{ margin: 0 }}>
                  Сначала начните урок — тогда здесь можно выдать ДЗ и писать заметки.
                  Списки задач выдаются во вкладке «Работа с учеником» (кнопка «Выдать задачи»).
                </p>
              </div>
            )}
            <LessonQuickItemsPanel
              activeSessionId={activeSession?.id || null}
              studentId={studentId}
              relationActive={relationActive}
              onChanged={() => bumpJournalRefresh()}
            />
          </div>

          <p className="text-secondary" style={{ fontSize: '0.85rem' }}>
            Карточки ниже — как у ученика. Крестик убирает пункт домашнего задания
            и после завершения урока. У теста пропадает и список у ученика.
          </p>
          <LessonJournal
            mode="teacher"
            studentId={studentId}
            refreshNonce={journalRefreshNonce}
            onHomeworkRemoved={() => {
              void loadHomeworkPackage();
              void loadOverview({ silent: true });
            }}
            onSelectTaskSlug={(slug) => {
              if (!slug) return;
              setSelectedListId(null);
              setSelectedTaskSlug(slug);
              switchWorkTab('student');
            }}
          />
      </div>

      <div
        className="teacher-topics-tab"
        hidden={workTab !== 'topics'}
        inert={workTab !== 'topics'}
      >
          <p className="text-secondary" style={{ marginTop: 0 }}>
            Заметки всех преподавателей с активной связью с этим учеником.
            Вы редактируете только свою заметку — она сохраняется автоматически.
            Клик по задаче из журнала тегов открывает её во вкладке «Работа с учеником».
          </p>
          <StudentTopicProgressPanel
            studentId={studentId}
            canEdit
            showJournal
            onSelectTaskSlug={(slug) => {
              if (!slug) return;
              setSelectedListId(null);
              setSelectedTaskSlug(slug);
              switchWorkTab('student');
            }}
          />
      </div>

      {/* При необходимости можно перевести на WorkPageBlocksLayout с блоками:
          текст задачи, список задач (с управлением), код попытки, комментарий, вердикт, блоки управления списками. */}
      <div
        className={`teacher-student-work-layout${workTab === 'prep' ? ' teacher-student-work-layout--prep' : ''}`}
        hidden={workTab === 'journal' || workTab === 'topics'}
        inert={workTab === 'journal' || workTab === 'topics'}
      >
        <aside className="teacher-work-left">
          <CollapsibleLeftSection
            sectionId="lists"
            title="Списки задач"
            open={leftSectionsOpen.lists}
            onToggle={(id) => setLeftSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
          >
            <div className="teacher-list-scope-row">
              <label className="text-secondary teacher-list-scope-label" htmlFor="teacher-list-scope-select">Источник</label>
              <select
                id="teacher-list-scope-select"
                className="form-select teacher-list-scope-select"
                value={listsScope}
                onChange={(e) => {
                  const v = e.target.value;
                  setListsScope(v);
                  const merged = [...myLists, ...otherLists, ...selfLists].sort((a, b) => {
                    const ta = a.issued_at ? new Date(a.issued_at).getTime() : 0;
                    const tb = b.issued_at ? new Date(b.issued_at).getTime() : 0;
                    return tb - ta;
                  });
                  const pool = v === 'all' ? merged : v === 'mine' ? myLists : v === 'other' ? otherLists : selfLists;
                  const filtered = listsStatusFilter === 'in_progress'
                    ? pool.filter((l) => l.student_status === 'IN_PROGRESS')
                    : pool;
                  const next = filtered[0] ?? pool[0] ?? null;
                  setSelectedListId(next?.id ?? null);
                }}
              >
                <option value="all">Все</option>
                <option value="mine">Выдано мной ({myLists.length})</option>
                <option value="other">Другие ({otherLists.length})</option>
                <option value="self">Самостоятельно ({selfLists.length})</option>
              </select>
              <div className="teacher-list-status-toggle" role="group" aria-label="Статус списков">
                <button
                  type="button"
                  className={`btn btn-xs ${listsStatusFilter === 'all' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setListsStatusFilter('all')}
                >
                  Все
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${listsStatusFilter === 'in_progress' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setListsStatusFilter('in_progress')}
                >
                  В работе
                </button>
              </div>
            </div>

            {activeLists.length === 0 ? (
              <p className="text-secondary">Нет списков в этой категории.</p>
            ) : (
              <ul className="task-list-tabs">
                {activeLists.map((lst) => (
                  <li key={lst.id}>
                    <button
                      type="button"
                      className={`task-list-tab-row ${selectedListId === lst.id ? 'active' : ''}`}
                      onClick={() => setSelectedListId(lst.id)}
                    >
                      <span className="task-list-tab-main">
                        <span className="task-list-tab-title">{lst.title}</span>
                        <span className="task-list-tab-dates text-secondary">
                          Выдано: {fmtListDateTime(lst.issued_at)}
                          {' · '}
                          Дедлайн: {fmtListDateTime(lst.deadline_at)}
                        </span>
                      </span>
                      <span className="list-status">
                        {lst.student_status === 'DONE' ? '✓' : '…'}
                        {lst.student_finished_at ? ' Сдано' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleLeftSection>

          {selectedList && (
            <CollapsibleLeftSection
              sectionId="tasks"
              title="Задачи в списке"
              open={leftSectionsOpen.tasks}
              onToggle={(id) => setLeftSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {selectedListWorkUrl && (
                    <Link to={selectedListWorkUrl} className="btn btn-xs btn-outline">
                      Открыть в работе
                    </Link>
                  )}
                  <button
                    type="button"
                    className="btn btn-xs btn-outline"
                    disabled={agendaSaving || !activeSession}
                    title={
                      !activeSession
                        ? 'Сначала начните урок'
                        : 'Добавить в занятие задачи с отчётом −, −− или +?'
                    }
                    onClick={() => void addProblemHwToLesson()}
                  >
                    {agendaSaving ? '…' : 'Плохие в занятие'}
                  </button>
                  {problemHwMsg ? (
                    <span className="text-secondary" style={{ fontSize: '0.85rem' }}>{problemHwMsg}</span>
                  ) : null}
                </div>
              </div>
              <ul className="task-list-items task-list-items-editable">
                {tasksInList.map((item, idx) => {
                  const t = item.task;
                  const slug = t ? taskNavRef(t) : (item.task_id != null ? String(item.task_id) : null);
                  const taskId = t?.id ?? item.task_id;
                  const report = item.latest_attempt?.self_report_status ?? item.latest_self_report?.self_report_status;
                  const label = report ? SELF_REPORT_LABELS[report] || report : '!';
                  const statusClass = report === 'EASY' || report === 'OK_NO_PEEK' || report === 'OK_WITH_PEEK' || report === 'OK_WITH_QUESTION'
                    ? 'report-positive'
                    : report === 'FAILED' || report === 'NO_IDEAS'
                    ? 'report-negative'
                    : 'report-neutral';
                  const inLesson = taskId != null && lessonTaskIdsForPrep.includes(taskId);
                  const rowLabel = `${idx + 1}. ${taskListLabel(t, { fallback: slug || `#${item.id}` })}`;
                  return (
                    <li key={item.id} className={selectedTaskSlug === slug ? 'current' : ''}>
                      <button
                        type="button"
                        className="task-list-item-btn"
                        onClick={() => setSelectedTaskSlug(slug)}
                      >
                        <span className="task-title">{rowLabel}</span>
                        <span className={`report-badge ${statusClass}`}>{label}</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline task-list-item-to-lesson"
                        title={
                          !activeSession
                            ? 'Сначала начните урок'
                            : inLesson
                              ? 'Уже в занятии'
                              : 'Добавить в занятие'
                        }
                        disabled={agendaSaving || !activeSession || inLesson || taskId == null}
                        onClick={() => addToAgenda('lesson', taskId)}
                      >
                        В занятие
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-outline task-list-item-remove"
                        title="Удалить из списка"
                        disabled={listSaving}
                        onClick={() => removeFromTaskList(taskId)}
                      >
                        ✕
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="task-list-add-block">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setListAddOpen((o) => !o)}
                >
                  {listAddOpen ? 'Свернуть' : '+ Добавить задачу в список'}
                </button>
                {listAddOpen && (
                  <div className="task-list-add-inline">
                    <div className="prep-search-row">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Поиск по названию или slug..."
                        value={listAddSearch}
                        onChange={(e) => setListAddSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchListAddTasks()}
                      />
                      <button type="button" className="btn btn-outline" onClick={searchListAddTasks} disabled={listAddSearching}>
                        {listAddSearching ? 'Поиск…' : 'Найти'}
                      </button>
                    </div>
                    {listAddResults.length > 0 && (
                      <ul className="prep-task-results list-add-results">
                        {listAddResults.slice(0, 15).map((t) => {
                          const inList = tasksInList.some((it) => (it.task?.id ?? it.task_id) === t.id);
                          return (
                            <li key={t.id}>
                              <span>{t.title || t.slug}</span>
                              <button
                                type="button"
                                className="btn btn-xs btn-outline"
                                disabled={listSaving || inList}
                                onClick={() => addToTaskList(t.id)}
                              >
                                {inList ? 'В списке' : 'Добавить'}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </CollapsibleLeftSection>
          )}

          <CollapsibleLeftSection
            sectionId="recent"
            title="Последние попытки"
            open={leftSectionsOpen.recent}
            onToggle={(id) => setLeftSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
          >
            {overview.recent_attempts.length === 0 ? (
              <p className="text-secondary">Нет попыток</p>
            ) : (
              <ul className="recent-attempts-list">
                {overview.recent_attempts.slice(0, 15).map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      className="link-like"
                      onClick={() => setSelectedTaskSlug(a.task_slug)}
                    >
                      {a.task_title || a.task_slug} <span className="badge-small">{SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || '!'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CollapsibleLeftSection>
        </aside>

        <main className="teacher-work-main">
          {/* Keep prep picker mounted so search results/scroll survive tab switches. */}
          <div
            className="teacher-prep-tab"
            style={workTab === 'prep' ? undefined : { display: 'none' }}
          >
            <LessonPrepTaskPicker
              studentId={studentId}
              lessonTaskIds={lessonTaskIdsForPrep}
              homeworkTaskIds={homeworkTaskIdsForPrep}
              canAdd={Boolean(activeSession?.id)}
              onAdd={addToAgenda}
              adding={agendaSaving}
            />
          </div>
          <div style={workTab === 'prep' ? { display: 'none' } : undefined}>
            {!selectedTaskSlug ? (
              <div className="empty-selection">
                <p className="text-secondary">Выберите задачу слева, из занятия или из ДЗ.</p>
              </div>
            ) : (
              <>
                <div className="task-statement-block">
                  <div className="task-statement-head">
                    <h2>{taskDetail?.title || selectedTaskSlug}</h2>
                    <Link
                      className="btn btn-outline btn-sm"
                      to={`/work/${taskNavRef(taskDetail) || selectedTaskSlug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Открыть для запуска
                    </Link>
                  </div>
                  <MarkdownText className="statement-text" text={taskDetail?.statement || 'Загрузка...'} />
                </div>

                {attempts.length > 0 && (
                  <div className="attempt-selector">
                    <label>Попытка:</label>
                    <select
                      value={selectedAttemptId || ''}
                      onChange={(e) => {
                        const id = Number(e.target.value);
                        setSelectedAttemptId(id);
                        const a = attempts.find((x) => x.id === id);
                        setAttemptDetail(a || null);
                      }}
                    >
                      {attempts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.created_at ? new Date(a.created_at).toLocaleString('ru-RU') : a.id} — {SELF_REPORT_LABELS[a.self_report_status] || '!'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {attemptDetail && (
                  <div className="attempt-code-block">
                    <div className="attempt-code-block-head">
                      <h3>Код решения</h3>
                      <span className="attempt-code-block-actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          onClick={() => void promoteAttemptToOfficial()}
                          disabled={officialBusy || officialSynced || !attemptCodeStr.trim()}
                          title={officialBtnTitle}
                        >
                          {officialBusy ? 'Сохранение…' : 'В эталон'}
                        </button>
                        {officialOverlayMsg ? (
                          <span
                            className={officialError ? 'text-danger' : 'text-success'}
                            style={{ fontSize: '0.85rem' }}
                            aria-live="polite"
                          >
                            {officialOverlayMsg}
                          </span>
                        ) : null}
                      </span>
                    </div>
                    {attemptCodeStr.trim()
                      ? <ReadOnlyPythonCode value={attemptDetail.code_snapshot} />
                      : <p className="text-secondary code-snapshot-empty">(пусто)</p>}
                  </div>
                )}

                {taskDetail ? (
                  <div className="attempt-code-block etalon-code-block">
                    <h3>Эталон</h3>
                    {etalonVariants.length === 0 ? (
                      <p className="text-secondary code-snapshot-empty">Эталона нет</p>
                    ) : (
                      <>
                        {etalonVariants.length > 1 ? (
                          <div className="solution-variants-tabs" role="tablist" aria-label="Варианты эталона">
                            {etalonVariants.map((v, i) => (
                              <button
                                key={`${v.label}-${i}`}
                                type="button"
                                role="tab"
                                aria-selected={i === etalonVariantIdx}
                                className={`btn btn-outline btn-xs solution-variant-tab ${i === etalonVariantIdx ? 'active' : ''}`}
                                onClick={() => setEtalonVariantIdx(i)}
                              >
                                {v.label}
                              </button>
                            ))}
                          </div>
                        ) : null}
                        {activeEtalon?.code ? (
                          <ReadOnlyPythonCode value={activeEtalon.code} />
                        ) : (
                          <p className="text-secondary code-snapshot-empty">Эталона нет</p>
                        )}
                      </>
                    )}
                  </div>
                ) : null}

                {attemptDetail && (
                  <>
                    {attemptDetail.self_comment && (
                      <div className="self-comment-block">
                        <strong>Комментарий ученика:</strong>
                        <p>{attemptDetail.self_comment}</p>
                      </div>
                    )}
                    <div className="teacher-comment-block">
                      <label>Комментарий преподавателя</label>
                      <textarea
                        className="form-input"
                        rows={3}
                        value={commentDraft}
                        onChange={(e) => setCommentDraft(e.target.value)}
                        placeholder="Сохраняется автоматически"
                      />
                      <span className="save-indicator">
                        {savingComment ? 'Сохранение…' : commentSaved ? '✓ Сохранено' : ''}
                      </span>
                    </div>
                    <div className="verdict-and-similar">
                      {selectedList && !selectedList.is_lesson_work_list ? (
                        <div className="verdict-buttons">
                          {(['ACCEPTED', 'REVIEW_IN_LESSON', 'REDO']).map((v) => (
                            <button
                              key={v}
                              type="button"
                              className={`btn btn-sm ${attemptDetail.teacher_verdict === v ? 'btn-primary' : 'btn-outline'}`}
                              onClick={() => setVerdict(v)}
                            >
                              {VERDICT_LABELS[v]}
                            </button>
                          ))}
                        </div>
                      ) : null}
                      {similarTasks.length > 0 && (
                        <div className="similar-tasks-near-verdict">
                          <div className="text-secondary similar-tasks-near-verdict-label">Аналоги</div>
                          <ul className="similar-tasks-near-verdict-list">
                            {similarTasks.map((st) => (
                              <li key={st.id}>
                                <Link to={`/work/${taskNavRef(st)}`} className="link-like similar-task-title">
                                  {st.title || taskNavRef(st)}
                                </Link>
                                {activeSession ? (
                                  <span className="similar-task-agenda-btns">
                                    <button
                                      type="button"
                                      className="btn btn-xs btn-outline"
                                      disabled={agendaSaving}
                                      onClick={() => addToAgenda('lesson', st.id)}
                                    >
                                      В занятие
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-xs btn-outline"
                                      disabled={agendaSaving}
                                      onClick={() => addToAgenda('homework', st.id)}
                                    >
                                      В ДЗ
                                    </button>
                                  </span>
                                ) : (
                                  <span className="text-secondary similar-task-no-session" title="Начните урок">
                                    Начните урок
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="add-to-lists">
                      {currentTaskId ? (
                        <button
                          type="button"
                          className={`btn btn-sm${inboxItemId ? ' btn-primary' : ' btn-outline'}`}
                          disabled={inboxBusy}
                          onClick={toggleTaskInbox}
                          title={inboxItemId ? 'Убрать из очереди на рассмотрение' : 'Отложить задачу в очередь на рассмотрение'}
                          aria-pressed={Boolean(inboxItemId)}
                        >
                          {inboxItemId ? 'В очереди' : 'В очередь'}
                        </button>
                      ) : null}
                      {activeSession && currentTaskId && (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            disabled={agendaSaving}
                            onClick={() => addToAgenda('lesson', currentTaskId)}
                          >
                            Разберём на занятии
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            disabled={agendaSaving}
                            onClick={() => addToAgenda('homework', currentTaskId)}
                          >
                            В новую ДЗ
                          </button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </main>

        <aside className="teacher-work-right">
          <CollapsibleLeftSection
            sectionId="lesson"
            title="Задачи для занятия"
            open={rightSectionsOpen.lesson}
            onToggle={(id) => setRightSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
          >
            <div className="agenda-block">
              <p className="text-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Попадают в список урока ученика — оттуда он открывает задачи, и можно подключиться.
              </p>
              {lessonAgenda.length === 0 ? (
                <p className="text-secondary">Пока пусто. Вкладка «Подготовка» → найти задачу → «В занятие».</p>
              ) : (
                <ul className="agenda-list agenda-list-editable">
                  {lessonAgenda.map((item, idx) => (
                    <li
                      key={`${item.task_id}-${idx}`}
                      className={item.slug && selectedTaskSlug === item.slug ? 'current' : ''}
                    >
                      <AgendaOrderControls
                        index={idx}
                        total={lessonAgenda.length}
                        disabled={agendaSaving}
                        onMove={(direction) => void moveInAgenda('lesson', idx, direction)}
                      />
                      {item.slug ? (
                        <button
                          type="button"
                          className="agenda-item-btn"
                          onClick={() => {
                            setSelectedTaskSlug(item.slug);
                            switchWorkTab('student');
                          }}
                        >
                          {agendaItemLabel(item)}
                        </button>
                      ) : (
                        <span>{agendaItemLabel(item)}</span>
                      )}
                      <button
                        type="button"
                        className="btn btn-xs btn-outline agenda-remove"
                        title="Убрать из занятия"
                        disabled={agendaSaving}
                        onClick={() => removeFromAgenda('lesson', item.task_id)}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CollapsibleLeftSection>
          <CollapsibleLeftSection
            sectionId="draft"
            title="Черновик списка задач"
            open={rightSectionsOpen.draft}
            onToggle={(id) => setRightSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
          >
            <div className="agenda-block">
              <p className="text-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Список задач у ученика появится после «Выдать задачи». Урок завершать не нужно.
                Тесты, конспекты и тренажёры — во вкладке «Журнал занятий».
              </p>
              {homeworkDraft.length === 0 ? (
                <p className="text-secondary">Пока пусто. Вкладка «Подготовка» → найти задачу → «В ДЗ».</p>
              ) : (
                <>
                  <ul className="agenda-list agenda-list-editable">
                    {homeworkDraft.map((item, idx) => (
                      <li
                        key={`${item.task_id}-${idx}`}
                        className={item.slug && selectedTaskSlug === item.slug ? 'current' : ''}
                      >
                        <AgendaOrderControls
                          index={idx}
                          total={homeworkDraft.length}
                          disabled={agendaSaving}
                          onMove={(direction) => void moveInAgenda('homework', idx, direction)}
                        />
                        {item.slug ? (
                          <button
                            type="button"
                            className="agenda-item-btn"
                            onClick={() => {
                              setSelectedTaskSlug(item.slug);
                              switchWorkTab('student');
                            }}
                          >
                            {agendaItemLabel(item)}
                          </button>
                        ) : (
                          <span>{agendaItemLabel(item)}</span>
                        )}
                        <button
                          type="button"
                          className="btn btn-xs btn-outline agenda-remove"
                          title="Убрать из черновика"
                          disabled={agendaSaving}
                          onClick={() => removeFromAgenda('homework', item.task_id)}
                        >
                          ✕
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    style={{ marginTop: '0.5rem' }}
                    disabled={issueSaving}
                    onClick={() => openIssueDialog(activeSession.id)}
                  >
                    Выдать задачи
                  </button>
                </>
              )}
            </div>
          </CollapsibleLeftSection>
          {!activeSession && leftoverSession && leftoverDraft.length > 0 && (
            <CollapsibleLeftSection
              sectionId="leftover"
              title={`Черновик с урока #${leftoverSession.id}`}
              open={rightSectionsOpen.leftover}
              onToggle={(id) => setRightSectionsOpen((o) => ({ ...o, [id]: !o[id] }))}
            >
              <div className="agenda-block">
                <p className="text-secondary" style={{ marginBottom: '0.5rem' }}>
                  Не выдано при завершении. Можно выдать список задач сейчас или очистить.
                </p>
                <ul className="agenda-list agenda-list-editable">
                  {leftoverDraft.map((item, idx) => (
                    <li key={`leftover-${item.task_id}-${idx}`}>
                      {item.slug ? (
                        <Link to={`/work/${item.slug}`} className="link-like">
                          {agendaItemLabel(item)}
                        </Link>
                      ) : (
                        <span>{agendaItemLabel(item)}</span>
                      )}
                    </li>
                  ))}
                </ul>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={issueSaving}
                    onClick={() => openIssueDialog(leftoverSession.id)}
                  >
                    Выдать задачи
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    disabled={agendaSaving}
                    onClick={() => void clearLeftoverDraft()}
                  >
                    Очистить
                  </button>
                </div>
              </div>
            </CollapsibleLeftSection>
          )}
          <div className="agenda-block topic-plan-block">
            <h4>
              <button type="button" className="link-like heading-toggle" onClick={() => { setTopicPlanOpen((o) => !o); if (!topicPlanOpen && topicPlan.length === 0) loadTopicPlan(); }}>
                План по темам {topicPlanOpen ? '▼' : '▶'}
              </button>
            </h4>
            {topicPlanOpen && (
              topicPlanLoading ? (
                <p className="text-secondary">Загрузка...</p>
              ) : topicPlan.length === 0 ? (
                <p className="text-secondary">Пока нет записей. Добавить темы можно через админку или позже из интерфейса.</p>
              ) : (
                <ul className="topic-plan-list">
                  {topicPlan.map((p) => (
                    <li key={p.id}>
                      <span className="topic-name">{p.tag_name || p.tag_slug}</span>
                      <span className={`topic-status status-${p.status}`}>{p.status === 'studied' ? 'Изучено' : p.status === 'planned' ? 'Планируется' : 'Заморожено'}</span>
                      {p.note && <span className="text-secondary">— {p.note}</span>}
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>
        </aside>
      </div>
      </div>

      <Modal
        open={issueDialogOpen}
        title={issueAndEnd ? 'Выдать задачи и завершить урок' : 'Выдать задачи'}
        onClose={() => {
          if (issueSaving) return;
          setIssueDialogOpen(false);
          setIssueSessionId(null);
          setIssueAndEnd(false);
        }}
      >
        <label className="form-label">
          Название
          <input
            type="text"
            className="form-input"
            value={issueTitle}
            onChange={(e) => setIssueTitle(e.target.value)}
          />
        </label>
        <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
          Дедлайн (необязательно)
          <input
            type="date"
            className="form-input"
            value={issueDeadline}
            onChange={(e) => setIssueDeadline(e.target.value)}
          />
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={issueSaving}
            onClick={() => void submitIssueHomework()}
          >
            {issueSaving ? '…' : (issueAndEnd ? 'Выдать задачи и завершить' : 'Выдать задачи')}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={issueSaving}
            onClick={() => {
              setIssueDialogOpen(false);
              setIssueSessionId(null);
              setIssueAndEnd(false);
            }}
          >
            Отмена
          </button>
        </div>
      </Modal>

      <Modal
        open={quizDialogOpen}
        title="Выдать тест"
        onClose={() => {
          if (quizSaving) return;
          setQuizDialogOpen(false);
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!quizSaving && !quizLoading && quizSelectedIds.length > 0) void submitIssueQuiz();
          }}
        >
        {quizLoading ? (
          <p className="text-secondary">Загрузка тестов…</p>
        ) : quizOptions.length === 0 ? (
          <p className="text-secondary">Нет заготовок тестов.</p>
        ) : (
          <>
            <AssignCheckboxList
              groups={quizPickerGroups}
              selectedIds={quizSelectedIds}
              onChange={handleQuizSelection}
              searchPlaceholder="Поиск теста…"
              emptyText="Нет тестов по запросу."
              disabled={quizSaving}
            />
            {quizSelectedIds.length === 1 ? (
              <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
                Название списка
                <input
                  type="text"
                  className="form-input"
                  value={quizTitle}
                  onChange={(e) => setQuizTitle(e.target.value)}
                />
              </label>
            ) : null}
            <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
              Дедлайн (необязательно)
              <input
                type="date"
                className="form-input"
                value={quizDeadline}
                onChange={(e) => setQuizDeadline(e.target.value)}
              />
            </label>
            <label className="checkbox-row" style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <input
                type="checkbox"
                checked={quizShuffle}
                onChange={(e) => setQuizShuffle(e.target.checked)}
              />
              Перемешать вопросы
            </label>
          </>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={quizSaving || quizLoading || quizSelectedIds.length === 0}
          >
            {quizSaving ? '…' : quizSelectedIds.length > 1 ? `Выдать (${quizSelectedIds.length})` : 'Выдать'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={quizSaving}
            onClick={() => setQuizDialogOpen(false)}
          >
            Отмена
          </button>
        </div>
        </form>
      </Modal>

      <Modal
        open={trainerDialogOpen}
        title="Выдать тренажёр"
        onClose={() => {
          if (trainerSaving) return;
          setTrainerDialogOpen(false);
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (trainerSaving || trainerSelectedIds.length === 0) return;
            if (
              trainerSelectedIds.length === 1
              && (trainerMetaLoading || !isTrainerConfigReady(trainerMeta, trainerConfig))
            ) return;
            void submitIssueTrainer();
          }}
        >
        <p className="text-secondary" style={{ marginBottom: '0.75rem' }}>
          Ученик увидит назначение в разделе «Тренажёры», не в списке ДЗ.
        </p>
        {trainerKinds.length === 0 ? (
          <p className="text-secondary">Загрузка каталога…</p>
        ) : (
          <AssignCheckboxList
            groups={trainerPickerGroups}
            selectedIds={trainerSelectedIds}
            onChange={handleTrainerSelection}
            searchPlaceholder="Поиск тренажёра…"
            emptyText="Нет тренажёров по запросу."
            disabled={trainerSaving}
          />
        )}
        {trainerSelectedIds.length > 1 ? (
          <p className="text-secondary" style={{ marginTop: '0.75rem', fontSize: '0.9rem' }}>
            Несколько тренажёров — стандартные настройки. Для своих параметров выдайте по одному.
          </p>
        ) : null}
        {trainerSelectedIds.length === 1 ? (
          trainerMetaLoading ? (
            <p className="text-secondary" style={{ marginTop: '0.75rem' }}>Загрузка настроек…</p>
          ) : (
            <>
              <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
                Название (необязательно)
                <input
                  type="text"
                  className="form-input"
                  value={trainerTitle}
                  onChange={(e) => setTrainerTitle(e.target.value)}
                  placeholder={trainerMeta?.title || 'Регулярки'}
                />
              </label>
              <div style={{ marginTop: '0.75rem' }}>
                <TrainerConfigForm
                  meta={trainerMeta}
                  config={trainerConfig}
                  onChange={setTrainerConfig}
                  disabled={trainerSaving}
                />
              </div>
            </>
          )
        ) : null}
        {trainerSelectedIds.length > 0 ? (
          <label className="form-label" style={{ display: 'block', marginTop: '0.75rem' }}>
            Дедлайн (необязательно)
            <input
              type="date"
              className="form-input"
              value={trainerDeadline}
              onChange={(e) => setTrainerDeadline(e.target.value)}
            />
          </label>
        ) : null}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              trainerSaving
              || trainerSelectedIds.length === 0
              || (
                trainerSelectedIds.length === 1
                && (trainerMetaLoading || !isTrainerConfigReady(trainerMeta, trainerConfig))
              )
            }
          >
            {trainerSaving
              ? '…'
              : trainerSelectedIds.length > 1
                ? `Выдать (${trainerSelectedIds.length})`
                : 'Выдать'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={trainerSaving}
            onClick={() => setTrainerDialogOpen(false)}
          >
            Отмена
          </button>
        </div>
        </form>
      </Modal>

      <Modal
        open={readingDialogOpen}
        title="Выдать конспекты"
        onClose={() => {
          if (readingSaving) return;
          setReadingDialogOpen(false);
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!readingSaving && !readingLoading && readingSelectedIds.length > 0) {
              void submitAddReadings();
            }
          }}
        >
        <p className="text-secondary" style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
          Конспект сразу попадёт в карточку текущего урока в журнале. Можно отметить несколько.
        </p>
        {readingSeriesTags.length > 0 ? (
          <div className="assign-reading-series" role="group" aria-label="Серии">
            <button
              type="button"
              className={!readingTag ? 'konspekty-scope-btn active' : 'konspekty-scope-btn'}
              disabled={readingLoading || readingSaving}
              onClick={() => void selectReadingTag('')}
            >
              Все серии
            </button>
            {readingSeriesTags.map((t) => (
              <button
                key={t.slug}
                type="button"
                className={readingTag === t.slug ? 'konspekty-scope-btn active' : 'konspekty-scope-btn'}
                disabled={readingLoading || readingSaving}
                onClick={() => void selectReadingTag(t.slug)}
              >
                {t.name}
              </button>
            ))}
          </div>
        ) : null}
        {readingLoading ? (
          <p className="text-secondary">Загрузка конспектов…</p>
        ) : (
          <AssignCheckboxList
            groups={readingPickerGroups}
            selectedIds={readingSelectedIds}
            onChange={setReadingSelectedIds}
            searchPlaceholder="Поиск конспекта…"
            emptyText="Нет конспектов по запросу."
            disabled={readingSaving}
          />
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={readingSaving || readingLoading || readingSelectedIds.length === 0}
          >
            {readingSaving
              ? '…'
              : readingSelectedIds.length > 1
                ? `Выдать (${readingSelectedIds.length})`
                : 'Выдать'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={readingSaving}
            onClick={() => setReadingDialogOpen(false)}
          >
            Отмена
          </button>
        </div>
        </form>
      </Modal>

      <Modal
        open={endDialogOpen && Boolean(activeSession) && !issueDialogOpen}
        title="В черновике есть задачи"
        onClose={() => {
          if (endingLesson || issueSaving) return;
          setEndDialogOpen(false);
        }}
      >
        <p className="text-secondary">
          Выдать список задач сейчас или завершить урок без выдачи (черновик останется на карточке)?
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={issueSaving || endingLesson}
            onClick={() => openIssueDialog(activeSession.id, { andEnd: true })}
          >
            Выдать задачи и завершить…
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={issueSaving || endingLesson}
            onClick={() => void endLessonWithoutIssue()}
          >
            {endingLesson ? '…' : 'Завершить без выдачи'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={issueSaving || endingLesson}
            onClick={() => setEndDialogOpen(false)}
          >
            Отмена
          </button>
        </div>
      </Modal>
    </div>
  );
}
